import config from '../config/env.config';

/**
 * Streaming Helper for SSE
 * Handles Server-Sent Events streaming from backend
 */

/**
 * Generate chat response with SSE streaming
 * @param {Object} payload - Request payload
 * @param {Function} onChunk - Callback for each text chunk
 * @returns {Promise<Object>} - Complete response
 */
export async function generateStreamingResponse(payload, onChunk) {
    const url = `${config.apiBaseUrl}${config.endpoints.chat.generate}`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-API-Key': config.apiKey,
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle SSE streaming
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';
    let result = {
        candidates: [{
            content: {
                parts: [{ text: '' }]
            }
        }]
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
            if (line.startsWith('data: ')) {
                try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'chunk' || data.status === 'chunk') {
                        fullText += data.text;
                        if (onChunk) {
                            onChunk(data.text, fullText, data.timestamp);
                        }
                    } else if (data.type === 'complete' || data.status === 'complete') {
                        // Log performance metrics
                        if (data.metrics) {
                            console.log('📊 Streaming Performance:');
                            console.log(`   Total time: ${data.metrics.totalTime}ms`);
                            console.log(`   First chunk: ${data.metrics.firstChunkTime}ms`);
                            console.log(`   Streaming time: ${data.metrics.streamingTime}ms`);
                            console.log(`   Chunks received: ${data.metrics.chunkCount}`);
                            console.log(`   Avg chunk time: ${data.metrics.avgChunkTime.toFixed(1)}ms`);
                        }
                        
                        result = {
                            candidates: [{
                                content: {
                                    parts: [{ text: data.text }]
                                }
                            }],
                            unspscResults: data.unspscResults,
                            outOfScope: data.outOfScope,
                            usedFallback: data.usedFallback,
                            metrics: data.metrics
                        };
                    }
                } catch (e) {
                    console.error('Error parsing SSE data:', e);
                }
            }
        }
    }

    // If no complete message received, use accumulated text
    if (!result.candidates[0].content.parts[0].text) {
        result.candidates[0].content.parts[0].text = fullText;
    }

    return result;
}
